"use client"

import { useTheme } from "next-themes"
import { useEffect, useState } from "react"
import { Sun, Moon, Palette, Leaf, ChevronDown, Check } from "lucide-react"

interface ThemeSwitcherProps {
  onThemeChange?: (theme: "light" | "dark") => void
}

export function ThemeSwitcher({ onThemeChange }: ThemeSwitcherProps) {
  const [mounted, setMounted] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { theme, setTheme } = useTheme()

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  const themes = [
    { id: "sahara", label: "Sahara Desert", icon: Sun, color: "text-orange-500", description: "Warm & bright" },
    { id: "mediterranean", label: "Mediterranean", icon: Moon, color: "text-blue-500", description: "Cool & calm" },
    { id: "islamic", label: "Islamic Heritage", icon: Palette, color: "text-emerald-500", description: "Rich & elegant" },
    { id: "nature", label: "Nature", icon: Leaf, color: "text-green-500", description: "Fresh & natural" },
  ]

  const currentTheme = themes.find((t) => t.id === theme)
  const CurrentIcon = currentTheme?.icon || Sun

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme)
    setIsOpen(false)
    if (onThemeChange) {
      const isDark = newTheme !== "sahara" && newTheme !== "mediterranean"
      onThemeChange(isDark ? "dark" : "light")
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 sm:gap-2 text-white hover:text-white/90 transition-all bg-white/15 hover:bg-white/25 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-white/20 hover:border-white/30 min-w-[44px] min-h-[44px] justify-center"
        aria-label="Theme selector"
      >
        <CurrentIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
        <span className="hidden md:inline text-xs font-medium truncate max-w-16">
          {currentTheme?.label.split(" ")[0]}
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-64 z-50 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="absolute -top-1 right-4 w-3 h-3 bg-white dark:bg-gray-900 rotate-45 border-l border-t border-gray-200 dark:border-gray-700"></div>
            
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                Choose Theme
              </div>
              
              <div className="py-1">
                {themes.map((t) => {
                  const IconComponent = t.icon
                  return (
                    <button
                      key={t.id}
                      onClick={() => handleThemeChange(t.id)}
                      className={`w-full px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between rounded-lg mx-1 ${
                        theme === t.id ? "bg-primary/10 text-primary dark:bg-primary/20" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent className={`w-4 h-4 ${theme === t.id ? 'text-primary' : t.color}`} />
                        <div className="flex flex-col items-start">
                          <span className="font-medium text-sm">{t.label}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t.description}</span>
                        </div>
                      </div>
                      {theme === t.id && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
